// Filename: DataWarehouseApproveFormPage.tsx
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Container, Grid, TextField, Typography, IconButton, Divider,
    Breadcrumbs, Link, Modal
} from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { useAuth } from "../../../helpers/AuthProvider";
import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields";
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from "../../MiniBot";
import MainDrawer from "../../MainDrawer";
import { Close } from '@mui/icons-material';

// --- Validation Schemas ---
const approvalValidationSchema = Yup.object().shape({
    serviceAccount: Yup.string().email("Must be a valid service account email").required("This field is required"),
});

const rejectValidationSchema = Yup.object().shape({
    reasonForRejection: Yup.string()
        .required("This field is required")
        .matches(/^[a-zA-Z0-9-\s.,!?()']*$/, "Comments can only contain alphanumeric characters and basic punctuation.")
        .strict(),
});

// --- Helper Component ---
const DetailItem: React.FC<{ label: string; value?: string | number | null; }> = ({ label, value }) => {
    const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
    return (
        <Box>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{displayValue}</Typography>
        </Box>
    );
};

// --- Main Content Component ---
const DataWarehousesApproveFormPageContent: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const [job, setJob] = useState<any>(null);
    const [openRejectModal, setOpenRejectModal] = useState(false);
    const [openApproveModal, setOpenApproveModal] = useState(false);

    useEffect(() => {
        const fetchJobData = async (jobId: string) => {
            try {
                const data = await queryGraphql(
                    `
                    query GetDataWarehouse($id: String!) {
                      getDataWarehouse(id: $id) {
                        id
                        name
                        cloudProvider
                        solutionCategoryType
                        status
                        createdAt
                        reasonForRejection
                        solutionOwner { name }
                        details {
                          dataWarehouse {
                            projectId
                            datasetId
                            region
                            pricingModel
                            serviceAccount
                          }
                        }
                        application {
                          id
                          applicationName
                        }
                      }
                    }
                    `,
                    { id: jobId }
                );
                if (data.getDataWarehouse) {
                    setJob(data.getDataWarehouse);
                }
            } catch (error) {
                console.log("Error fetching data warehouse details:", error);
            }
        };
        if (id) fetchJobData(id);
    }, [id]);

    const getStatusInfo = (status: string | undefined) => {
        if (!status) return { text: 'Unknown', color: '#FDB02210', icon: '/assets/clock.svg' };
        const lowerStatus = status.toLowerCase();
        if (["active", "approved", "success"].includes(lowerStatus)) return { text: 'Active', color: '#12B76A10', icon: '/assets/tickcircle.svg' };
        if (["rejected", "failed"].includes(lowerStatus)) return { text: 'Rejected', color: '#F0443810', icon: '/assets/reject.svg' };
        return { text: 'Pending', color: '#FDB02210', icon: '/assets/clock.svg' };
    };
    const statusInfo = getStatusInfo(job?.status);

    return (
        <Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
            <MiniBot />
            <Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                    <Link underline="hover" color="inherit" href="/datasolutions/requests/data-warehouses">
                        Requests
                    </Link>
                    <Typography color="text.primary">Request Details</Typography>
                </Breadcrumbs>
                <Box display="flex" alignItems="center" mt={1}>
                    <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/datasolutions/requests/data-warehouses")}>
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="18px">
                        Request Details
                    </Typography>
                </Box>
            </Box>
            <Grid container>
                <Grid item xs={0.5} />
                <Grid item xs={11}>
                    {job && (
                        <Formik
                            initialValues={{ serviceAccount: '' }}
                            validationSchema={approvalValidationSchema}
                            onSubmit={async (values) => {
                                try {
                                    await queryGraphql(
                                        `mutation ApproveSolutionDataWarehouse($input: ApproveSolutionInput!) {
                                            approveSolutionDataWarehouse(input: $input) { id }
                                        }`,
                                        { input: {
                                            id: job.id,
                                            details: {
                                                dataWarehouse: {
                                                    serviceAccount: values.serviceAccount
                                                }
                                            },
                                        }}
                                    );
                                    navigate("/datasolutions/requests/data-warehouses");
                                } catch (err) {
                                    console.error("Approval failed:", err);
                                }
                            }}
                        >
                            {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
                                <Form id="job-approve-form">
                                    <Box mx={4}>
                                        <Typography fontSize="16px" my={1}>
                                            Review the data warehouse request and fill the details to approve.
                                        </Typography>
                                        <Box>
                                            <Box bgcolor={statusInfo.color} justifyContent="space-between" alignItems="center" display="flex" borderRadius="12px 12px 0px 0px" p={2} boxShadow={1}>
                                                <Box>
                                                    <Typography fontSize="16px" fontFamily="Gilroy-Bold">{job.name}</Typography>
                                                    <Typography fontFamily="Gilroy-Regular" fontSize="12px">{`Requested By: ${job.solutionOwner?.name} | On: ${moment(Number(job.createdAt)).format("DD/MM/YYYY")}`}</Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <img src={statusInfo.icon} alt="Status" style={{ width: "20px", marginRight: "8px" }} />
                                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">{statusInfo.text}</Typography>
                                                </Box>
                                            </Box>
                                            <Box bgcolor="#fff" p={3} borderRadius="0px 0px 12px 12px" boxShadow={1}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} sm={4}><DetailItem label="Cloud Provider" value={job.cloudProvider?.toUpperCase()} /></Grid>
                                                    <Grid item xs={12} sm={4}><DetailItem label="Application" value={job.application?.applicationName} /></Grid>
                                                    <Grid item xs={12} sm={4}><DetailItem label="Warehouse Type" value={job.solutionCategoryType} /></Grid>
                                                    <Grid item xs={12} sm={4}><DetailItem label="Region" value={job.details?.dataWarehouse?.region} /></Grid>
                                                    <Grid item xs={12} sm={4}><DetailItem label="Pricing Model" value={job.details?.dataWarehouse?.pricingModel} /></Grid>
                                                    <Grid item xs={12} sm={4}><DetailItem label="Project/Account ID" value={job.details?.dataWarehouse?.projectId} /></Grid>
                                                    {job.details?.dataWarehouse?.datasetId && <Grid item xs={12} sm={4}><DetailItem label="Dataset ID" value={job.details?.dataWarehouse?.datasetId} /></Grid>}
                                                </Grid>
                                            </Box>

                                            {job.status === "pending" && user.roles.some((element: string) => ["infrastructure_admin"].includes(element)) && (
                                                <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                                                    <Typography fontSize="14px" fontFamily="Gilroy-Bold">Approver Details</Typography>
                                                    <Divider sx={{ marginY: 2 }} />
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12}>
                                                            <CompulsoryFieldsLabel fieldName="Service Account" />
                                                            <TextField fullWidth name="serviceAccount" value={values.serviceAccount} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel placeholder="Enter service account email" error={touched.serviceAccount && Boolean(errors.serviceAccount)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                            <ErrorMessage name="serviceAccount">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            )}
                                            
                                            {(job.status !== "pending" && job.details?.dataWarehouse?.serviceAccount) && (
                                                <Box bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1} mt={2}>
                                                    <Typography fontSize="14px" fontFamily="Gilroy-Bold">Approved Details</Typography>
                                                    <Divider sx={{ marginY: 2 }} />
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12}><DetailItem label="Service Account" value={job.details.dataWarehouse.serviceAccount} /></Grid>
                                                    </Grid>
                                                </Box>
                                            )}

                                            {job.status === "rejected" && (
                                                 <Box bgcolor="#fff" p={2} borderRadius="12px" boxShadow={1} mt={2}>
                                                    <DetailItem label="Reason for Rejection" value={job.reasonForRejection} />
                                                 </Box>
                                            )}

                                            {job.status === "pending" && user.roles.some((element: string) => ["infrastructure_admin"].includes(element)) && (
                                                <Box display="flex" justifyContent="flex-end" gap={2} mt={4} pb={4}>
                                                    <Button sx={{color: "black",borderColor: "black",fontWeight: "bold",textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} variant="outlined" size="large" onClick={() => setOpenRejectModal(true)}>Reject Request</Button>
                                                    <Button sx={{backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} 
                                                            variant="contained" 
                                                            size="large"  disabled={!isValid || !dirty} onClick={() => setOpenApproveModal(true)}>Approve Request</Button>
                                                </Box>
                                            )}
                                        </Box>

                                        <Modal open={openApproveModal} onClose={() => setOpenApproveModal(false)}>
                                            <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: {xs: '90%', md: 600}, bgcolor: "white", borderRadius: "16px", boxShadow: 24 }}>
                                                <Box display="flex" justifyContent="space-between" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Approve Data Warehouse Request</Typography><IconButton onClick={() => setOpenApproveModal(false)}><Close /></IconButton></Box>
                                                <Divider />
                                                <Typography p={3}>Are you sure you want to approve this Data Warehouse Request?</Typography>
                                                <Divider />
                                                <Box sx={{ p: 2, textAlign: "right" }}>
                                                    <Button type="submit" form="job-approve-form" sx={{backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} 
                                                            variant="contained" 
                                                            size="large">Approve</Button>
                                                </Box>
                                            </Box>
                                        </Modal>
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    )}
                </Grid>
                <Grid item xs={0.5} />
            </Grid>

            <Modal open={openRejectModal} onClose={() => setOpenRejectModal(false)}>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: {xs: '90%', md: 700}, bgcolor: "white", borderRadius: "16px", boxShadow: 24 }}>
                    <Formik
                        initialValues={{ reasonForRejection: "" }}
                        validationSchema={rejectValidationSchema}
                        onSubmit={async (values) => {
                            try {
                                await queryGraphql(`mutation RejectSolutionDataWarehouse($input: RejectSolutionInput!) { rejectSolutionDataWarehouse(input: $input) { id } }`, { input: { id: job.id, reasonForRejection: values.reasonForRejection } });
                                navigate("/datasolutions/requests/data-warehouses");
                            } catch (err) { console.error("Rejection failed:", err); }
                        }}
                    >
                        {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
                            <Form id="job-reject-form">
                                <Box display="flex" justifyContent="space-between" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Reject Data Warehouse Request</Typography><IconButton onClick={() => setOpenRejectModal(false)}><Close /></IconButton></Box>
                                <Divider />
                                <Box p={3}>
                                    <CompulsoryFieldsLabel fieldName="Comments" />
                                    <TextField fullWidth multiline rows={4} name="reasonForRejection" value={values.reasonForRejection} onChange={handleChange} onBlur={handleBlur} placeholder="Enter comments for rejection..." hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} error={touched.reasonForRejection && Boolean(errors.reasonForRejection)} />
                                    <ErrorMessage name="reasonForRejection">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
                                </Box>
                                <Divider />
                                <Box sx={{ p: 2, textAlign: "right" }}>
                                    <Button type="submit" form="job-reject-form" variant="contained" disabled={!isValid || !dirty} sx={{backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} >Reject</Button>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Box>
            </Modal>
        </Container>
    );
};


// --- Page Shell Component ---
const DataWarehousesApproveFormPage: React.FC = () => {
    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"requests-data-warehouses"} />
            <Box component="main" sx={{ flexGrow: 1, p: 0, bgcolor: '#fafafa' }}>
                <DataWarehousesApproveFormPageContent />
            </Box>
        </Box>
    );
};

export default DataWarehousesApproveFormPage;